import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSave, FaArrowLeft } from 'react-icons/fa';
import { api } from '../../App';
import { AdminLayout } from './AdminDashboard';
import RichTextEditor from '../../components/RichTextEditor';
import './Admin.css';

const AdminPostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    category: 'general',
    featured_image: '',
    status: 'draft',
    meta_title: '',
    meta_description: ''
  });

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const categories = [
    { value: 'general', label: 'Splošno' },
    { value: 'weekly-report', label: 'Tedensko poročilo' },
    { value: 'market-analysis', label: 'Analiza trga' },
    { value: 'news', label: 'Novice' },
    { value: 'education', label: 'Izobraževanje' }
  ];

  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    try {
      const data = await api.get(`/api/admin/posts/${id}`);
      setFormData({
        title: data.post.title || '',
        slug: data.post.slug || '',
        content: data.post.content || '',
        excerpt: data.post.excerpt || '',
        category: data.post.category || 'general',
        featured_image: data.post.featured_image || '',
        status: data.post.status || 'draft',
        meta_title: data.post.meta_title || '',
        meta_description: data.post.meta_description || ''
      });
    } catch (error) {
      console.error('Failed to fetch post:', error);
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate slug from title
    if (name === 'title' && !isEditing) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({
        ...prev,
        slug
      }));
    }
  };

  const handleSubmit = async (e, saveStatus = formData.status) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const postData = {
        ...formData,
        status: saveStatus
      };

      if (isEditing) {
        await api.patch(`/api/admin/posts/${id}`, postData);
      } else {
        await api.post('/api/admin/posts', postData);
      }

      navigate('/admin/posts');
    } catch (error) {
      console.error('Failed to save post:', error);
      setError(error.message || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEditing ? 'Uredi objavo' : 'Nova objava'}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Nalaganje objave...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? 'Uredi objavo' : 'Nova objava'}>
      <motion.div
        className="editor-form"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <button 
          onClick={() => navigate('/admin/posts')}
          className="btn btn-ghost"
          style={{ marginBottom: '24px' }}
        >
          <FaArrowLeft /> Nazaj na objave
        </button>

        {error && (
          <div className="form-error" style={{ marginBottom: '24px' }}>
            {error}
          </div>
        )}

        <form onSubmit={(e) => handleSubmit(e)}>
          <div className="editor-grid">
            {/* Main Content */}
            <div className="editor-main">
              <div className="editor-panel">
                <div className="form-group">
                  <label className="form-label">Naslov *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Vnesite naslov objave"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">URL Končnica *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="url-naslov-objave"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Vsebina *</label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                    placeholder="Začnite pisati vsebino objave tukaj..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Povzetek</label>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="Kratek opis objave"
                    rows="3"
                  />
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="editor-sidebar-panel">
              {/* Publish Options */}
              <div className="editor-panel">
                <h3>Objava</h3>
                
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="draft">Osnutek</option>
                    <option value="published">Objavljeno</option>
                    <option value="archived">Arhivirano</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Kategorija</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="form-select"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="editor-actions">
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'draft')}
                    className="btn btn-secondary"
                    disabled={saving}
                  >
                    Shrani osnutek
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'published')}
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    <FaSave /> {saving ? 'Shranjujem...' : 'Objavi'}
                  </button>
                </div>
              </div>

              {/* Featured Image */}
              <div className="editor-panel">
                <h3>Naslovna slika</h3>
                <div className="form-group">
                  <label className="form-label">URL slike</label>
                  <input
                    type="url"
                    name="featured_image"
                    value={formData.featured_image}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="https://example.com/slika.jpg"
                  />
                </div>
                {formData.featured_image && (
                  <div style={{ marginTop: '12px' }}>
                    <img 
                      src={formData.featured_image} 
                      alt="Preview" 
                      style={{ 
                        width: '100%', 
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                      }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              {/* SEO Settings */}
              <div className="editor-panel">
                <h3>SEO nastavitve</h3>
                
                <div className="form-group">
                  <label className="form-label">Meta naslov</label>
                  <input
                    type="text"
                    name="meta_title"
                    value={formData.meta_title}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Naslov za iskalnike"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Meta opis</label>
                  <textarea
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="Opis za iskalnike"
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </motion.div>
    </AdminLayout>
  );
};

export default AdminPostEditor;

